/**
 * API Client
 *
 * Axios instance configured for the Sentinel API with:
 * - Base URL configuration
 * - JWT token injection
 * - Error handling
 * - Response transformation
 *
 * Uses the auth store for token management.
 */
import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { getActivePinia } from 'pinia'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { authService, staffApiOrigin } from './auth.service'
import { isPortalIdentityMode } from './auth-mode'
import { isSigningOut } from './sign-in-redirect'
import { notifyError } from './toastBridge'
import { tokenExpiresAtMs } from '../views/auth/reset-token-expiry'
import { findDemoHandler } from './demo/handlers'
import {
  makeDemoAdapter,
  makeDemoCrashReportBlobAdapter,
  applyDemoGetMerge,
} from './demo/adapter'

// Per-request marker: set once we've already attempted a silent refresh+retry
// for this exact request, so a second 401 doesn't loop.
/**
 * A request that handles its own failure in the view and does not want the
 * global toast. Everything else about the interceptor still runs — the silent
 * background report and the `cs:api-error` event are deliberately NOT skipped,
 * because suppressing the toast must not also suppress the telemetry.
 */
export interface SilenceableRequestConfig extends InternalAxiosRequestConfig {
  suppressErrorToast?: boolean
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

// Module-level single-flight guard. While a refresh is in progress, concurrent
// 401s await the SAME promise instead of each firing their own /auth/refresh
// (which would race the single-use rotation and revoke each other's tokens).
let refreshPromise: Promise<string> | null = null

/**
 * Fallback paths a 401 must never evict someone from when router metadata
 * is unavailable or does not explicitly mark the current route as public.
 *
 * These pages are public and hold nothing behind auth, so there is nothing to
 * protect by bouncing the visitor to /login — and doing it strands them. The
 * case that surfaced it (2026-08-15): an invitee opens their emailed
 * /reset-password link in a browser still carrying an EXPIRED token, the auth
 * store validates that token on mount, the 401 lands, and the redirect throws
 * them onto /login — where they have no password yet, and the email link they
 * arrived on has already been consumed by the redirect. `clearAuth()` still
 * runs (the stale token SHOULD go); only the navigation is suppressed, which
 * leaves the store's own graceful catch in stores/auth.ts to finish the job.
 *
 * Exact paths, not prefixes: every entry is a real route in all three routers
 * (portal, ops, marketing/apply), and a prefix test would also swallow any
 * future authenticated route that merely starts with one of these strings.
 *
 * They are compared against the ROUTE path, not `window.location.pathname` —
 * see `currentRoutePath()`. Apply is served under a base of `/apply/`, so its
 * real pathname is `/apply/reset-password` and a raw exact match silently
 * excludes the app this fix was written for.
 */
const PUBLIC_AUTH_PATHS = ['/login', '/reset-password', '/forgot-password']

/**
 * `window.location.pathname` with the app's Vite base removed, so it can be
 * compared against router-declared paths.
 *
 * BASE_URL is `/` for portal and ops and `/apply/` for the apply app
 * (`packages/marketing/vite.config.ts` → `base: process.env.VITE_BASE || '/'`),
 * which is why the raw pathname and the route path agree on two of the three
 * surfaces and diverge on the third. Verified against production 2026-08-16:
 * apply serves the set-password screen at `/apply/reset-password`.
 */
function currentRoutePath(): string {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '')
  const path = window.location.pathname
  if (!base) return path
  if (path === base) return '/'
  return path.startsWith(base + '/') ? path.slice(base.length) : path
}

/**
 * An app's own router, when it has registered one. Preferred over the lazy
 * lookup below: `getActivePinia()._a` is a Pinia INTERNAL, so if it ever moves
 * this silently degrades to the string fallback and W5 quietly comes back.
 * Registration makes that impossible for the app that registers.
 *
 * Optional by design — an app that never calls it still works.
 */
let registeredRouter: Router | null = null

export function registerAppRouter(router: Router): void {
  registeredRouter = router
}

/** Does this route declare itself public? Both conventions in use across the
 *  five apps: portal marks `public: true`, ops/apply/driver-portal use only
 *  `requiresAuth: false`. A route with no meta at all is treated as private. */
function routeDeclaresPublic(router: Router): boolean {
  const route = router.resolve(router.options.history.location)
  return (
    route.matched.length > 0 &&
    route.meta.requiresAuth !== true &&
    (route.meta.public === true || route.meta.requiresAuth === false)
  )
}

/** Exported for `tests/public-route-401.spec.ts`; not part of the package's
 *  public surface (absent from index.ts) because apps have no reason to ask. */
export function isCurrentRoutePublic(): boolean {
  try {
    if (registeredRouter) return routeDeclaresPublic(registeredRouter)

    // Pinia retains its installed app in _a. Resolve lazily so an app that has
    // not registered still gets the fix.
    const app = (getActivePinia() as { _a?: App } | undefined)?._a
    const router = app?.config.globalProperties.$router as Router | undefined
    if (router?.currentRoute.value.matched.length) {
      // History supplies the current URL without the app base (including hash
      // routers), rather than a possibly stale route during navigation.
      if (routeDeclaresPublic(router)) return true
    }
  } catch {
    // Missing app/router internals or failed resolution keep the old behavior.
  }
  return PUBLIC_AUTH_PATHS.includes(currentRoutePath())
}

/** A request that got no usable answer: no response at all, or a 5xx. */
function isUnreachable(error: unknown): boolean {
  const e = error as { isAxiosError?: boolean; response?: { status?: number } } | null
  if (!e?.isAxiosError) return false
  const status = e.response?.status
  return status === undefined || status >= 500
}

/**
 * Attempt to obtain a new access token via the rotating refresh token.
 * De-duplicates concurrent callers onto one in-flight request. Resolves with
 * the new access token on success; rejects if there is no refresh token or the
 * refresh call fails.
 */
function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  const authStore = useAuthStore()
  const currentRefreshToken = authStore.refreshToken
  if (!currentRefreshToken) {
    return Promise.reject(new Error('No refresh token available'))
  }

  refreshPromise = authService
    .refresh(currentRefreshToken)
    .then((pair) => {
      authStore.setTokens(pair.access_token, pair.refresh_token)
      return pair.access_token
    })
    .finally(() => {
      // Clear the single-flight slot whether it succeeded or failed so the
      // next genuine 401 can start a fresh attempt.
      refreshPromise = null
    })

  return refreshPromise
}

/**
 * An access token with at least `minValiditySeconds` left to live, refreshing
 * through the same single-flight refresh as the 401 interceptor when the
 * current one is closer to expiry than that (or unreadable).
 *
 * For handing a token to a page on another origin that cannot refresh on its
 * own — ops' driver-app setup page on api.claimshieldpro.com asks for one after
 * a 401 (`ops/src/services/setupPageTokenBridge.ts`). Rejects when no refresh
 * is possible; the caller decides what to tell the other page.
 */
export async function getFreshAccessToken(minValiditySeconds = 120): Promise<string> {
  const current = useAuthStore().token
  const expiresAt = current ? tokenExpiresAtMs(current) : null
  if (current && expiresAt !== null && expiresAt - Date.now() > minValiditySeconds * 1000) {
    return current
  }
  return refreshAccessToken()
}

// API base URL - uses Vite proxy in development
const API_BASE_URL = import.meta.env.DEV ? '/api' : 'https://api.claimshieldpro.com/api'

// Absolute URL for error reporting (native fetch, not axios)
const ERROR_REPORT_URL = import.meta.env.DEV
  ? '/api/v1/frontend-errors'
  : 'https://api.claimshieldpro.com/api/v1/frontend-errors'

/**
 * Silently report an API error to the backend for logging and Cliq alerts.
 * Uses native fetch() to avoid re-entering the axios interceptor.
 * Fire-and-forget — failures are swallowed.
 */
function reportErrorSilently(error: AxiosError): void {
  try {
    if (useAuthStore().mode === 'oidc') return
    // This independent fetch cannot carry the customer session CSRF token.
    // Explicit staff view-as keeps its legacy reporting path.
    if (isPortalIdentityMode() && !useAuthStore().staffViewAsContext) return
    // Skip cancelled requests — these fire when a user navigates away or
    // swaps context (e.g. View As) before the previous call resolves.
    // Not actionable, pure noise in the alerts channel.
    if (axios.isCancel(error) || error.code === 'ERR_CANCELED') return

    const url = error.config?.url || ''
    // Loop prevention: never report errors about the error-reporting endpoint
    if (url.includes('/frontend-errors')) return
    // Skip noisy statuses that aren't actionable
    const status = error.response?.status
    if (status === 400 || status === 401 || status === 410 || status === 422) return

    const authStore = useAuthStore()
    const errorData = error.response?.data as Record<string, unknown> | undefined
    const errorMessage =
      (errorData?.detail as string) || error.message || 'Unknown error'

    fetch(ERROR_REPORT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        error_message: String(errorMessage).slice(0, 2000),
        http_status: status ?? null,
        failed_endpoint: url || null,
        http_method: error.config?.method?.toUpperCase() || null,
        user_email: authStore.user?.email || null,
        page_path: window.location.pathname,
        timestamp: new Date().toISOString(),
      }),
      keepalive: true,
    })
    // No .then(), no .catch() — fire and forget
  } catch {
    // Swallow any synchronous errors (e.g. serialization)
  }
}

/**
 * Create and configure the Axios instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor - add auth token
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Get token from auth store
      // Note: We access the store inside the interceptor to get fresh token
      const authStore = useAuthStore()
      if (authStore.mode === 'oidc') {
        config.baseURL = `${staffApiOrigin}/api`
        config.withCredentials = true
        delete config.headers.Authorization
        const method = (config.method || 'get').toLowerCase()
        if (!['get', 'head', 'options'].includes(method)) {
          const csrf = authStore.staffSession?.csrf_token
          if (!csrf) return Promise.reject(new Error('Staff CSRF session unavailable'))
          config.headers['X-CSRF-Token'] = csrf
        }
        return config
      }
      if (isPortalIdentityMode()) config.baseURL = '/api'

      if (isPortalIdentityMode() && !authStore.staffViewAsContext) {
        config.withCredentials = true
        delete config.headers.Authorization
        const method = (config.method || 'get').toLowerCase()
        if (!['get', 'head', 'options'].includes(method)) {
          const csrf = authStore.portalSession?.csrf_token
          if (csrf) {
            config.headers['X-CSRF-Token'] = csrf
          } else if (!isCurrentRoutePublic()) {
            return Promise.reject(new Error('Portal CSRF session unavailable'))
          }
          // A public token page (audit pass, onboarding, e-sign, ...) has no
          // portal session by design: the token in its URL is the credential,
          // and the server checks CSRF itself on every cookie-authenticated
          // route. Refusing here stopped every audit-pass download in the
          // browser, shown as "Connection problem" (2026-10-20).
        }
        return config
      }

      if (authStore.token) {
        config.headers.Authorization = `Bearer ${authStore.token}`
      }

      // Portal staff "view-as": when a STAFF viewer has picked a client, scope
      // portal READ calls to that client by appending ?client_id=. Only the
      // portal sets viewAsClientId (ops never does), and the backend resolves
      // client_id via resolve_target_client, which IGNORES it for CLIENT-role
      // callers — so this can never cross-scope. Limited to portal client/fleet
      // endpoints, and skipped when the caller already set client_id (e.g. the
      // standalone /view-as token tab, which injects via provide/inject).
      const viewAsId = authStore.viewAsClientId
      if (viewAsId && authStore.isStaff) {
        const url = config.url || ''
        const portalScoped = url.includes('/v1/client/') || url.includes('/v1/fleet/')
        const alreadyHas =
          url.includes('client_id=') ||
          !!(config.params && 'client_id' in config.params)
        if (portalScoped && !alreadyHas) {
          config.params = { ...(config.params || {}), client_id: viewAsId }
        }
        // Edit-on-behalf: only when the staff viewer has explicitly toggled edit
        // mode. Signals write intent so the backend (resolve_client_for_write)
        // permits actions on the viewed client. Default off — reads never carry it.
        // NEVER sent in demo mode (belt: the store makes the two modes mutually
        // exclusive; suspenders: this guard) — demo must not be able to write.
        if (portalScoped && authStore.editOnBehalf && !authStore.demoMode) {
          config.headers['X-Edit-On-Behalf'] = '1'
        }

        // Demo mode: portal-scoped MUTATIONS with a registered demo handler are
        // answered locally by a fake adapter — the request never leaves the
        // browser. Unregistered mutations fall through WITHOUT the edit header,
        // so the server's staff-read-only 403 stays the safety backstop.
        const method = (config.method || 'get').toLowerCase()
        if (
          authStore.demoMode &&
          portalScoped &&
          method !== 'get' &&
          method !== 'head' &&
          method !== 'options'
        ) {
          const handler = findDemoHandler(method, url)
          if (handler) config.adapter = makeDemoAdapter(handler)
        }

        // One narrow demo GET exception: the crash-report blob of a DEMO-
        // fabricated accident (id demo-*). Such an id can only name a
        // fabricated row, so no real read is ever masked; without this,
        // "View" on a just-simulated upload would 403 mid-demo.
        if (
          authStore.demoMode &&
          portalScoped &&
          method === 'get' &&
          /\/v1\/client\/accidents\/demo-[^/]+\/crash-report/.test(url)
        ) {
          config.adapter = makeDemoCrashReportBlobAdapter(url)
        }
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  // Response interceptor - handle errors
  client.interceptors.response.use(
    (response) => {
      // Demo mode: layer session-fabricated effects onto real GET responses
      // (accident registry, driver-doc status) so simulated actions persist.
      const authStore = useAuthStore()
      if (
        authStore.demoMode &&
        (response.config.method || 'get').toLowerCase() === 'get'
      ) {
        return applyDemoGetMerge(response)
      }
      return response
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableRequestConfig | undefined

      // Handle 401 Unauthorized
      if (error.response?.status === 401) {
        if (useAuthStore().mode === 'oidc') {
          useAuthStore().clearAuth()
          if (!isCurrentRoutePublic() && !isSigningOut()) window.location.href = '/login?expired=1'
          return Promise.reject(error)
        }
        if (isPortalIdentityMode() && !useAuthStore().staffViewAsContext) {
          useAuthStore().clearAuth()
          if (!isCurrentRoutePublic() && !isSigningOut()) window.location.href = '/login?expired=1'
          return Promise.reject(error)
        }
        const requestUrl = originalRequest?.url || ''
        // Never attempt a silent refresh for the auth flow itself — a 401 from
        // login / login-with-refresh / refresh is a real credential/token
        // failure, not an expired access token.
        const isAuthFlow =
          requestUrl.includes('/auth/login') ||
          requestUrl.includes('/auth/login-with-refresh') ||
          requestUrl.includes('/auth/refresh')

        const authStore = useAuthStore()

        // Try exactly one silent refresh + retry per original request, only if
        // we have a refresh token and this isn't an auth-flow call.
        if (
          originalRequest &&
          !originalRequest._retried &&
          !isAuthFlow &&
          authStore.refreshToken
        ) {
          originalRequest._retried = true
          try {
            const newAccessToken = await refreshAccessToken()
            // Replay the original request with the fresh token.
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
            return client(originalRequest)
          } catch (refreshError) {
            // The refresh never got an answer (network drop, timeout, 5xx):
            // the refresh token may be perfectly good, so keep the sign-in and
            // let this request fail on its own. Ending a session because Wi-Fi
            // blinked logged staff out mid-work (ops audit V6, 2026-10-02).
            // Reject with the REFRESH error, not the original 401: a caller
            // (the session restore above all) must see "unreachable", or it
            // reads the 401 as the server refusing the sign-in and ends it.
            if (isUnreachable(refreshError)) return Promise.reject(refreshError)
            // Refresh was REFUSED (expired/revoked refresh token, or Redis-down
            // + bad token) — fall through to clearAuth + redirect below.
          }
        }

        // Refresh not possible or it failed — clear auth, even on public pages.
        authStore.clearAuth()

        // Only redirect if route metadata or the PUBLIC_AUTH_PATHS fallback
        // does not mark this page public. Preserve full path (query + hash) so
        // the user lands back where they were — matches the router guard,
        // which uses to.fullPath for the redirect query.
        //
        // KNOWN, PRE-EXISTING and deliberately not changed here: the redirect
        // TARGET below is base-unaware too, so on apply it points at `/login`
        // rather than `/apply/login`. That is shipped behaviour on a path this
        // session has not reproduced, and widening the fix to it would be a
        // second change riding an unrelated deploy. Flagged, not fixed.
        if (!isCurrentRoutePublic()) {
          const fullPath =
            window.location.pathname + window.location.search + window.location.hash
          window.location.href = `/login?redirect=${encodeURIComponent(fullPath)}`
        }

        return Promise.reject(error)
      }

      // Handle 403 Forbidden
      if (error.response?.status === 403) {
        console.error('[API] Forbidden - insufficient permissions')
        // Demo mode: an unregistered MUTATION hit the server backstop. Tell the
        // demoer plainly instead of leaving a silent read-only failure. Read
        // 403s are NOT this case — "nothing was changed" would mislabel them.
        const authStore403 = useAuthStore()
        const method403 = (originalRequest?.method || 'get').toLowerCase()
        if (authStore403.demoMode && !['get', 'head', 'options'].includes(method403)) {
          notifyError(
            'Not in the demo',
            'This action is not simulated in demo mode — nothing was changed.',
          )
        }
      }

      // Handle 404 Not Found
      if (error.response?.status === 404) {
        console.error('[API] Resource not found:', originalRequest?.url)
      }

      // Handle 422 Validation Error - log full details for debugging
      if (error.response?.status === 422) {
        console.error('[API] Validation error:', originalRequest?.method?.toUpperCase(), originalRequest?.url, error.response.data)
      }

      // Handle 500+ Server errors
      if (error.response?.status && error.response.status >= 500) {
        console.error('[API] Server error:', error.response.data)
      }

      // Handle network errors
      if (!error.response) {
        console.error('[API] Network error - server may be unreachable')
      }

      // User-facing toast for genuine failures.
      // Skip on: cancellations, auth flows, view-specific statuses, and timeouts
      // during initial page load (where the view's own error state is better UX).
      const isCancelled = axios.isCancel(error) || error.code === 'ERR_CANCELED'
      const status = error.response?.status
      const isNetwork = !error.response
      const isTimeout = error.code === 'ECONNABORTED'

      // Opt-out for a request whose caller shows a BETTER message in place.
      // Additive and per-request: a call that does not set it behaves exactly as
      // before. Added 2026-09-22 for the public audit pass, where a failed
      // download already reports itself next to the driver it belongs to,
      // naming what could not be retrieved — a generic "Something went wrong"
      // banner over the top of that is both less specific and, on a page whose
      // job is to be a calm record, the wrong register.
      const suppressed = (error.config as SilenceableRequestConfig | undefined)
        ?.suppressErrorToast === true

      if (!isCancelled && !suppressed) {
        if (status && status >= 500) {
          notifyError(
            'Something went wrong',
            "We hit an error on our end. Please try again — if it keeps happening, let us know.",
          )
        } else if (isTimeout) {
          notifyError(
            'Request timed out',
            'The server took too long to respond. Check your connection and try again.',
          )
        } else if (isNetwork) {
          notifyError(
            'Connection problem',
            "We couldn't reach the server. Check your internet connection and try again.",
          )
        }
      }

      // Silent background reporting (native fetch, fire-and-forget)
      reportErrorSilently(error)

      // App-agnostic hook: any app may subscribe (the ops error nudge does). No app coupling here.
      if (!isCancelled && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('cs:api-error', {
          detail: {
            status: error.response?.status ?? null,
            url: error.config?.url ?? null,
            method: error.config?.method ?? null,
            message: getApiErrorMessage(error),
          },
        }))
      }

      return Promise.reject(error)
    }
  )

  return client
}

// Export singleton instance
export const api = createApiClient()

/**
 * Type-safe API helpers
 */
export const apiHelpers = {
  /**
   * GET request with typed response
   */
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await api.get<T>(url, { params })
    return response.data
  },

  /**
   * POST request with typed response
   */
  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.post<T>(url, data)
    return response.data
  },

  /**
   * PUT request with typed response
   */
  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.put<T>(url, data)
    return response.data
  },

  /**
   * PATCH request with typed response
   */
  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await api.patch<T>(url, data)
    return response.data
  },

  /**
   * DELETE request with typed response
   */
  async delete<T>(url: string): Promise<T> {
    const response = await api.delete<T>(url)
    return response.data
  },
}

/**
 * Error type for API errors
 */
export interface ApiError {
  detail: string | Array<{ loc: string[]; msg: string; type: string }>
  status_code?: number
  errors?: Record<string, string[]>
}

/**
 * Extract error message from API error
 */
export function getApiErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    // No response at all — server unreachable, CORS, timeout, or rebuild window.
    // Surface a human-friendly hint instead of axios's raw "Network Error".
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        return 'Request timed out — the server took too long to respond. Try again in a moment.'
      }
      return 'Server is temporarily unavailable. This usually clears in 30 seconds — try again.'
    }

    const data = error.response.data as (ApiError & { error?: { message?: string; code?: string } }) | undefined

    // Sentinel APIResponse.fail shape: { success: false, error: { code, message, ... } }
    // Prefer this over the FastAPI `detail` fallback so the user sees the
    // handler's own wording instead of a raw status line.
    if (data?.error?.message) {
      return data.error.message
    }

    // FastAPI detail message
    if (data?.detail) {
      // Handle 422 validation errors (detail is an array of objects)
      if (Array.isArray(data.detail)) {
        return data.detail
          .map((e) => `${e.loc?.join('.') || 'field'}: ${e.msg}`)
          .join('; ')
      }
      return data.detail
    }

    // HTTP status text
    if (error.response.statusText) {
      return error.response.statusText
    }

    // Fallback
    if (error.message) {
      return error.message
    }
  }

  // Plain Error (e.g. thrown by service layer when API returns success=false)
  if (error instanceof Error && error.message) {
    return error.message
  }

  // Unknown error
  return 'An unexpected error occurred'
}

/**
 * Driver-facing error messaging helpers.
 *
 * Used in public driver flows (apply, sign, dqf-completion, employer-contact)
 * where the audience is a non-technical CDL driver on a phone, not a staff
 * member who can read a stack trace.
 *
 * Two entry points so it works for both axios- and fetch-based callers:
 *
 *   - getDriverFriendlyErrorMessage(error, opts)   — pass an AxiosError, Error,
 *     or anything getApiErrorMessage can interpret.
 *   - mapBackendDetailToFriendly(detail, status, opts) — pass the raw
 *     `json.detail` string + HTTP status when you already parsed the
 *     fetch Response yourself.
 *
 * Both share the same mapping table. The mapping is defense-in-depth: the
 * backend should already send a friendly `detail`, but if a stale deploy or
 * an unhandled 500 slips through, this layer still produces something the
 * driver can act on.
 *
 * Pass `{ contactHint: 'employer' | 'support' }` to control whether the
 * fallback tells the driver to contact their employer (signing flows) or
 * support@claimshieldpro.com (apply flow).
 */
export interface DriverFriendlyOpts {
  contactHint?: 'employer' | 'support'
}

function _contactLine(opts: DriverFriendlyOpts): string {
  return opts.contactHint === 'employer'
    ? "If this keeps happening, ask your employer to resend the link."
    : "If this keeps happening, email support@claimshieldpro.com."
}

/**
 * Map a backend `detail` string (and optional status) to a driver-friendly
 * message. Used directly by fetch-based views that parsed the response
 * themselves; also called internally by getDriverFriendlyErrorMessage.
 */
export function mapBackendDetailToFriendly(
  detail: string | null | undefined,
  status: number | undefined,
  opts: DriverFriendlyOpts = {},
): string {
  const contactLine = _contactLine(opts)
  const genericFallback = `Something went wrong. Please try again. ${contactLine}`

  // Missing detail on a 5xx → never expose the raw status code to a driver.
  if (!detail || !detail.trim()) {
    if (status && status >= 500) return genericFallback
    if (status === 404) return 'We couldn\'t find what you were looking for. Please double-check the link from your employer.'
    if (status === 410) return 'This link has expired or has already been used. Please ask your employer for a new one.'
    return genericFallback
  }

  const lower = detail.toLowerCase()

  // Generic placeholders or stale vague messages.
  if (
    lower === 'an error occurred' ||
    lower === 'internal server error' ||
    lower === 'an unexpected error occurred' ||
    lower.startsWith('failed to submit signature') ||
    lower.startsWith('failed to get form content') ||
    lower.startsWith('failed to create authorization bundle') ||
    lower.startsWith('failed to load')
  ) {
    return genericFallback
  }

  // Internal jargon that occasionally leaked from older code paths.
  if (
    lower.includes('cannot route') ||
    lower.includes('no linked driver record') ||
    lower.includes('no linked employee record')
  ) {
    return `We're having trouble saving that. Please try again. ${contactLine}`
  }
  if (lower.startsWith("unsupported document_type")) {
    return detail.replace(/^Unsupported document_type '[^']*'\.\s*/i, "That document type isn't supported here. ")
  }
  if (lower.startsWith('bundle not found or expired')) {
    return 'This signing link has expired or is no longer valid. Please ask your employer to send a new one.'
  }

  return detail
}

export function getDriverFriendlyErrorMessage(
  error: unknown,
  opts: DriverFriendlyOpts = {},
): string {
  // Pull status off the axios error directly when possible so we can apply
  // the status-aware fallback in mapBackendDetailToFriendly.
  let status: number | undefined
  if (axios.isAxiosError(error)) {
    status = error.response?.status
    // No response at all — getApiErrorMessage already returns a friendly
    // network message; just defer to it.
    if (!error.response) {
      return getApiErrorMessage(error)
    }
  }
  const baseMsg = getApiErrorMessage(error)
  return mapBackendDetailToFriendly(baseMsg, status, opts)
}

// Build marker — referenced below so the bundler keeps it, which forces a
// new content-hash on the shared services chunk and gets CF Pages' edge cache
// to stop serving the prior chunk's stale 404 SPA fallback (observed
// 2026-04-22 after the APIResponse.fail → 4xx rollout).
const __API_HELPER_BUILD = '2026-04-22T21:22-apiresponse-fail-4xx'
if (typeof window !== 'undefined') {
  ;(window as unknown as { __API_HELPER_BUILD?: string }).__API_HELPER_BUILD = __API_HELPER_BUILD
}
